<template>
  <AutoComplete
    v-model="category_name"
    data-test="document-category-select"
    dropdown
    optionLabel="textVal"
    :placeholder="$t('document.attributes.category')"
    :pt="{
      overlay: {
        class: '!z-[4250]',
      },
    }"
    size="small"
    :suggestions="categories_filtered"
    @clear="clear_input"
    @complete="search"
    @option-select="select"
  />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { LANGUAGES, LANGUAGES_CUSTOMER_ONLY } from '@/app/data/model_constants';
import { useI18n } from 'vue-i18n';
import AutoComplete from 'primevue/autocomplete';

const { t } = useI18n();

const props = defineProps({
  attachment_category_id: { type: String, default: null },
  with_tickets_and_decisions: { type: Boolean },
});

const emit = defineEmits(['select-category']);

const store = useStore();
const category_name = ref('');
const categories_filtered = ref([]);

const current_attachment_categories = computed(() => store.getters.current_attachment_categories);
const current_profile = computed(() => store.getters.current_profile);

const fallbackLanguageForCategory = (category) => {
  const translations = category.attributes.translations;
  return _.chain(LANGUAGES)
    .concat(LANGUAGES_CUSTOMER_ONLY)
    .filter((l) => l !== current_profile.value.attributes.language)
    .filter((l) => !_.isEmpty(translations[l]) && !_.isEmpty(translations[l].name))
    .value()[0];
};

const current_categories_translated = () =>
  _.map(current_attachment_categories.value, (c) => {
    if (_.isEmpty(c.attributes.name)) {
      const lg = fallbackLanguageForCategory(c);
      c.attributes.name = c.attributes.translations[lg].name;
    }
    return c;
  });

const categories = computed(() => {
  const categories_translated = current_categories_translated();
  const base_categories = _.map(categories_translated, (c) => ({
    textVal: c.attributes.name,
    enumVal: c.id,
    type: 'CATEGORY',
  }));

  if (!props.with_tickets_and_decisions) return _.sortBy(base_categories, 'textVal');

  const tickets_and_decisions = _.concat(base_categories);
  return _.sortBy(tickets_and_decisions, 'textVal');
});

const category_name_for_id = (id) => {
  if (!id) return '';
  const match = _.find(current_categories_translated(), ['id', id]);
  return match ? match.attributes.name : '';
};

const search = (event) => {
  categories_filtered.value = _.filter(
    categories.value,
    (c) => c.textVal.toLowerCase().indexOf(event.query.toLowerCase()) >= 0,
  );
};

const select = (event) => {
  emit('select-category', event.value.enumVal, event.value.type);
};

const clear_input = () => {
  emit('select-category', null, null);
};

watch(
  [() => props.attachment_category_id, current_attachment_categories],
  () => {
    category_name.value = category_name_for_id(props.attachment_category_id);
  },
  { immediate: true },
);
</script>
